import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  fetchEvalExperiment,
  fetchEvalExperimentArtifactView,
  fetchEvalTrialArtifactRaw,
  fetchEvalTrialArtifactView,
  fetchEvalTrialEnvelopes,
  fetchEvalTrialTranscriptView,
  isImageArtifact,
  isTextArtifact,
  isVideoArtifact,
  type EvalArtifactRef,
  type EvalExperimentRecord,
  type EvalExperimentSummary,
  type EvalFileViewResponse,
  type EvalGraderResult,
  type EvalRecordEnvelope,
  type EvalTaskAggregate,
  type EvalTrialRecord,
} from '@/admin/lib/api';
import { Breadcrumbs } from '@/admin/components/breadcrumbs';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/admin/components/ui/card';
import { Label } from '@/admin/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/admin/components/ui/select';
import { Skeleton } from '@/admin/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import { ScrollArea } from '@/admin/components/ui/scroll-area';
import {
  IconArrowLeft,
  IconDownload,
  IconFile,
  IconFileText,
  IconInfoCircle,
  IconPhoto,
  IconRefresh,
  IconRobot,
  IconTool,
  IconUser,
  IconVideo,
} from '@tabler/icons-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/admin/components/ui/tooltip';

type EvalExperimentEnvelope = EvalRecordEnvelope<
  EvalExperimentRecord,
  EvalExperimentSummary
>;
type EvalTrialEnvelope = EvalRecordEnvelope<EvalTrialRecord>;

type ContinuousLearningTrendPoint = {
  trialId: string;
  index: number;
  total?: number;
  score: number | null;
  pass: boolean | null;
  status?: string;
};

type ContinuousLearningTrend = {
  sequenceKey: string;
  taskRef: string;
  taskLabel: string;
  agentId?: string;
  agentGroupId?: string;
  total?: number;
  points: ContinuousLearningTrendPoint[];
  delta: number | null;
};

function formatDateTime(value?: string) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function formatDuration(startedAt?: string, completedAt?: string) {
  if (!startedAt || !completedAt) return '—';
  const durationMs =
    new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(durationMs) || durationMs < 0) return '—';
  if (durationMs < 1000) return `${durationMs}ms`;
  return `${(durationMs / 1000).toFixed(1)}s`;
}

function formatScore(value?: number | null) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return `${Math.round(value * 100)}%`;
}

function formatCount(value?: number) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return String(value);
}

function formatAverageTokens(value?: number) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  const rounded = Math.round(value);
  if (Math.abs(value - rounded) < 0.05) {
    return String(rounded);
  }
  return value.toFixed(1);
}

function isTrialPassing(record?: EvalTrialRecord | null) {
  const results = record?.grader_results || [];
  if (results.length === 0) return null;
  return results.every(result => result.pass);
}

function averageTrialScore(record?: EvalTrialRecord | null) {
  const results = record?.grader_results || [];
  if (results.length === 0) return null;
  const total = results.reduce(
    (sum, result) =>
      sum + (typeof result.score === 'number' ? result.score : 0),
    0
  );
  return total / results.length;
}

function formatGraderLabel(graderId?: string) {
  if (!graderId) return '—';
  const fileName = graderId.split('/').pop() || graderId;
  return fileName.replace(/\.ya?ml$/, '');
}

function formatTaskLabel(taskRef?: string) {
  if (!taskRef) return '—';
  const fileName = taskRef.split('/').slice(-2, -1)[0] || taskRef;
  return fileName;
}

function curveKeyOrder(key: string): [number, number] {
  const prefix = key.startsWith('pass_at_') ? 0 : 1;
  const k = Number.parseInt(key.split('_').pop() || '0', 10);
  return [prefix, k];
}

function extractMetricPairs(metrics?: Record<string, number>) {
  return Object.entries(metrics || {})
    .filter(
      ([key]) => key.startsWith('pass_at_') || key.startsWith('pass_hat_')
    )
    .sort(([left], [right]) => {
      const [lp, lk] = curveKeyOrder(left);
      const [rp, rk] = curveKeyOrder(right);
      if (lp !== rp) return lp - rp;
      return lk - rk;
    });
}

function formatCurveLabel(key: string): string {
  if (key.startsWith('pass_at_')) return `pass@${key.slice('pass_at_'.length)}`;
  if (key.startsWith('pass_hat_'))
    return `pass^${key.slice('pass_hat_'.length)}`;
  return key;
}

function collectCurveKeys(aggregates: EvalTaskAggregate[]): string[] {
  const keys = new Set<string>();
  for (const task of aggregates) {
    for (const key of Object.keys(task.aggregate_metrics || {})) {
      if (key.startsWith('pass_at_') || key.startsWith('pass_hat_')) {
        keys.add(key);
      }
    }
  }
  return [...keys].sort((left, right) => {
    const [lp, lk] = curveKeyOrder(left);
    const [rp, rk] = curveKeyOrder(right);
    if (lp !== rp) return lp - rp;
    return lk - rk;
  });
}

function summarizeExperimentGrading(trials: EvalTrialEnvelope[]) {
  const gradedTrials = trials.filter(
    trial => (trial.record.grader_results || []).length > 0
  );
  const passingTrials = gradedTrials.filter(trial =>
    isTrialPassing(trial.record)
  );
  const averageScores = gradedTrials
    .map(trial => averageTrialScore(trial.record))
    .filter((score): score is number => typeof score === 'number');

  const graderCount = gradedTrials.reduce(
    (count, trial) => count + (trial.record.grader_results?.length || 0),
    0
  );

  return {
    gradedTrialCount: gradedTrials.length,
    passingTrialCount: passingTrials.length,
    graderCount,
    averageScore:
      averageScores.length > 0
        ? averageScores.reduce((sum, score) => sum + score, 0) /
          averageScores.length
        : null,
  };
}

function prettyJson(input: string) {
  try {
    return JSON.stringify(JSON.parse(input), null, 2);
  } catch {
    return input;
  }
}

function extractOutcomeText(summary?: string) {
  if (!summary) return '—';
  try {
    const parsed = JSON.parse(summary) as { text?: string };
    if (typeof parsed.text === 'string' && parsed.text.trim()) {
      return parsed.text.trim();
    }
  } catch {
    // fall back to raw summary
  }
  return summary;
}

function inferTextMode(view: EvalFileViewResponse | null) {
  if (!view) return 'plain';
  const path = view.path.toLowerCase();
  const contentType = view.content_type.toLowerCase();
  if (path.endsWith('.md') || contentType.includes('markdown')) {
    return 'markdown';
  }
  if (
    path.endsWith('.json') ||
    contentType.includes('application/json') ||
    contentType.includes('application/yaml')
  ) {
    return 'structured';
  }
  return 'plain';
}

function FileViewer({
  title,
  description,
  view,
  loading,
  error,
  emptyState,
}: {
  title: string;
  description?: string;
  view: EvalFileViewResponse | null;
  loading: boolean;
  error: string | null;
  emptyState?: React.ReactNode;
}) {
  const mode = inferTextMode(view);

  return (
    <Card className="min-h-[280px]">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
        {view && (
          <p className="text-xs text-muted-foreground break-all">
            {view.path} · {view.size_bytes} bytes
          </p>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-[240px] w-full" />
        ) : error ? (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        ) : !view ? (
          emptyState || (
            <div className="text-sm text-muted-foreground">
              Nothing selected yet.
            </div>
          )
        ) : mode === 'markdown' ? (
          <ScrollArea className="h-[360px] rounded-md border bg-muted/10 p-4">
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <ReactMarkdown>{view.content}</ReactMarkdown>
            </div>
          </ScrollArea>
        ) : (
          <ScrollArea className="h-[360px] rounded-md border bg-muted/10">
            <pre className="whitespace-pre-wrap break-words p-4 text-xs leading-5">
              {mode === 'structured' ? prettyJson(view.content) : view.content}
            </pre>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}

interface TranscriptMessage {
  role?: string;
  content?: unknown;
  model?: string;
  input_tokens?: number;
  output_tokens?: number;
  tool_use_id?: string;
  metadata?: Record<string, unknown>;
  created_at?: number;
  message_id?: number;
  [key: string]: unknown;
}

interface ToolCallEntry {
  id?: string;
  type?: string;
  function?: { name?: string; arguments?: string };
}

function transcriptMessageKey(msg: TranscriptMessage) {
  return [
    msg.message_id ?? 'message',
    msg.created_at ?? 'timestamp',
    msg.role ?? 'role',
    msg.tool_use_id ?? 'tool-use',
  ].join(':');
}

function transcriptToolCallKey(entry: ToolCallEntry) {
  return [
    entry.id ?? 'call',
    entry.type ?? 'type',
    entry.function?.name ?? 'name',
    entry.function?.arguments ?? 'arguments',
  ].join(':');
}

function parseTranscriptMessages(raw: string): TranscriptMessage[] {
  const messages: TranscriptMessage[] = [];
  for (const line of raw.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    try {
      messages.push(JSON.parse(trimmed) as TranscriptMessage);
    } catch {
      // skip malformed lines
    }
  }
  return messages;
}

function resolveContent(raw: unknown): unknown {
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
  return raw;
}

function extractText(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .filter(
        (b): b is { type: string; text: string } =>
          typeof b === 'object' &&
          b !== null &&
          (b as Record<string, unknown>).type === 'text' &&
          typeof (b as Record<string, unknown>).text === 'string'
      )
      .map(b => b.text)
      .join('\n');
  }
  if (typeof content === 'object' && content !== null) {
    const obj = content as Record<string, unknown>;
    if (typeof obj.text === 'string') return obj.text;
  }
  return '';
}

function extractToolCalls(content: unknown): ToolCallEntry[] {
  if (typeof content !== 'object' || content === null) return [];
  const obj = content as Record<string, unknown>;
  if (!Array.isArray(obj.tool_calls)) return [];
  return obj.tool_calls as ToolCallEntry[];
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

function formatToolCallField(raw: string, maxLen = 1000): string {
  let s: string;
  try {
    s = JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    s = raw;
  }
  s = s.replace(/\\n/g, '\n').replace(/\\t/g, '\t');
  return truncate(s, maxLen);
}

type EvalTraceToolCall = NonNullable<EvalTrialRecord['tool_calls']>[number];

type TraceEntry =
  | {
      kind: 'message';
      timestampMs: number | null;
      sequence: number;
      message: TranscriptMessage;
    }
  | {
      kind: 'tool_call';
      timestampMs: number | null;
      sequence: number;
      call: EvalTraceToolCall;
    };

function parseTimestampMs(value?: string | number): number | null {
  if (typeof value === 'number') {
    const ms = value > 10_000_000_000 ? value : value * 1000;
    return Number.isFinite(ms) ? ms : null;
  }
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function buildTraceEntries(
  messages: TranscriptMessage[],
  toolCalls: EvalTraceToolCall[],
  verbose: boolean
): TraceEntry[] {
  const entries: TraceEntry[] = messages.map((message, index) => ({
    kind: 'message',
    timestampMs: parseTimestampMs(message.created_at),
    sequence: index,
    message,
  }));

  if (verbose) {
    entries.push(
      ...toolCalls.map((call, index) => ({
        kind: 'tool_call' as const,
        timestampMs: parseTimestampMs(call.timestamp),
        sequence: messages.length + index,
        call,
      }))
    );
  }

  return entries.sort((left, right) => {
    if (left.timestampMs !== null && right.timestampMs !== null) {
      if (left.timestampMs !== right.timestampMs) {
        return left.timestampMs - right.timestampMs;
      }
    } else if (left.timestampMs !== null) {
      return -1;
    } else if (right.timestampMs !== null) {
      return 1;
    }
    return left.sequence - right.sequence;
  });
}

function parseDeliveredFilePaths(record?: EvalTrialRecord | null): string[] {
  const raw = record?.environment_metadata?.delivered_file_paths;
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      return parsed
        .filter((value): value is string => typeof value === 'string')
        .map(value => value.trim())
        .filter(Boolean);
    }
  } catch {}

  return raw
    .split(/[\n,]/)
    .map(value => value.trim())
    .filter(Boolean);
}

function parsePositiveInt(value?: string): number | undefined {
  if (!value) return undefined;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function buildContinuousLearningTrends(
  trials: EvalTrialEnvelope[]
): ContinuousLearningTrend[] {
  const groups = new Map<string, ContinuousLearningTrend>();

  for (const trial of trials) {
    const record = trial.record;
    const metadata = record.environment_metadata || {};
    const sequenceKey = metadata.learning_sequence_key?.trim();
    if (!sequenceKey) continue;

    const taskRef = record.task_ref || sequenceKey;
    const index =
      parsePositiveInt(metadata.learning_sequence_index) ||
      record.attempt_index ||
      1;
    const total = parsePositiveInt(metadata.learning_sequence_total);
    const score = averageTrialScore(record);
    const pass = isTrialPassing(record);

    const existing =
      groups.get(sequenceKey) ||
      ({
        sequenceKey,
        taskRef,
        taskLabel: formatTaskLabel(taskRef),
        agentId: metadata.learning_agent_id || record.agent_id,
        agentGroupId: metadata.learning_agent_group_id,
        total,
        points: [],
        delta: null,
      } satisfies ContinuousLearningTrend);

    existing.total = existing.total || total;
    existing.agentId = existing.agentId || metadata.learning_agent_id;
    existing.agentGroupId =
      existing.agentGroupId || metadata.learning_agent_group_id;
    existing.points.push({
      trialId: record.id,
      index,
      total,
      score,
      pass,
      status: record.status,
    });
    groups.set(sequenceKey, existing);
  }

  return Array.from(groups.values())
    .map(group => {
      const points = group.points.sort((left, right) => {
        if (left.index !== right.index) return left.index - right.index;
        return left.trialId.localeCompare(right.trialId);
      });
      const scored = points.filter(
        (point): point is ContinuousLearningTrendPoint & { score: number } =>
          typeof point.score === 'number'
      );
      const delta =
        scored.length >= 2
          ? scored[scored.length - 1].score - scored[0].score
          : null;
      return { ...group, points, delta };
    })
    .sort((left, right) => left.taskRef.localeCompare(right.taskRef));
}

function formatToolResultContent(content: unknown): {
  label: string;
  detail: string;
} | null {
  if (typeof content === 'string') return { label: '', detail: content };
  if (typeof content !== 'object' || content === null) return null;
  const obj = content as Record<string, unknown>;

  if (typeof obj.exit_code === 'number') {
    const parts: string[] = [];
    if (typeof obj.stdout === 'string' && obj.stdout)
      parts.push(truncate(obj.stdout, 800));
    if (typeof obj.stderr === 'string' && obj.stderr)
      parts.push(`stderr: ${truncate(obj.stderr, 400)}`);
    return {
      label: `exit ${obj.exit_code}`,
      detail: parts.join('\n') || '(no output)',
    };
  }

  if (typeof obj.message === 'string')
    return { label: '', detail: obj.message };
  if (typeof obj.error === 'string')
    return { label: 'error', detail: obj.error };

  const json = JSON.stringify(content, null, 2);
  return { label: '', detail: truncate(json, 600) };
}

function roleIcon(role?: string) {
  switch (role) {
    case 'assistant':
      return <IconRobot className="h-4 w-4 shrink-0 text-blue-500" />;
    case 'user':
      return <IconUser className="h-4 w-4 shrink-0 text-green-500" />;
    case 'tool':
      return <IconTool className="h-4 w-4 shrink-0 text-orange-500" />;
    default:
      return <IconFile className="h-4 w-4 shrink-0 text-muted-foreground" />;
  }
}

function roleBadgeVariant(
  role?: string
): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (role) {
    case 'assistant':
      return 'default';
    case 'user':
      return 'secondary';
    default:
      return 'outline';
  }
}

function TranscriptMessageRow({
  msg,
  idx,
}: {
  msg: TranscriptMessage;
  idx: number;
}) {
  const content = useMemo(() => resolveContent(msg.content), [msg.content]);
  const text = extractText(content);
  const toolCalls = extractToolCalls(content);
  const toolResult =
    msg.role === 'tool' ? formatToolResultContent(content) : null;

  return (
    <div className="rounded-md border bg-muted/10 p-3">
      <div className="mb-1 flex items-center gap-2">
        {roleIcon(msg.role)}
        <Badge variant={roleBadgeVariant(msg.role)}>
          {msg.role || 'unknown'}
        </Badge>
        {msg.model && (
          <span className="text-xs text-muted-foreground">{msg.model}</span>
        )}
        {typeof msg.input_tokens === 'number' && (
          <span className="text-xs text-muted-foreground">
            {msg.input_tokens}→{msg.output_tokens ?? 0} tokens
          </span>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          #{idx + 1}
        </span>
      </div>

      {text && (
        <div className="mt-1 pl-6 text-sm whitespace-pre-wrap break-words">
          {truncate(text, 2000)}
        </div>
      )}

      {toolCalls.length > 0 && (
        <div className="mt-2 space-y-1.5 pl-6">
          {toolCalls.map(tc => (
            <div
              key={transcriptToolCallKey(tc)}
              className="rounded border bg-muted/20 px-3 py-1.5"
            >
              <div className="flex items-center gap-2 text-xs">
                <IconTool className="h-3 w-3 text-orange-500" />
                <span className="font-medium">
                  {tc.function?.name || 'unknown'}
                </span>
                {tc.id && (
                  <span className="font-mono text-muted-foreground">
                    {tc.id}
                  </span>
                )}
              </div>
              {tc.function?.arguments && (
                <pre className="mt-1 max-h-[80px] overflow-auto text-xs text-muted-foreground">
                  {(() => {
                    try {
                      return truncate(
                        JSON.stringify(
                          JSON.parse(tc.function.arguments),
                          null,
                          2
                        ),
                        500
                      );
                    } catch {
                      return truncate(tc.function.arguments, 500);
                    }
                  })()}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}

      {msg.role === 'tool' && (
        <div className="mt-1 pl-6">
          {toolResult && (
            <div className="mt-1">
              {toolResult.label && (
                <Badge
                  variant={
                    toolResult.label === 'exit 0' ? 'outline' : 'destructive'
                  }
                  className="mb-1 text-xs"
                >
                  {toolResult.label}
                </Badge>
              )}
              <pre className="max-h-[120px] overflow-auto rounded bg-muted/20 p-2 text-xs text-muted-foreground">
                {toolResult.detail}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TraceToolCallRow({
  call,
  idx,
}: {
  call: EvalTraceToolCall;
  idx: number;
}) {
  const hasDetail = Boolean(call.input || call.output);

  return (
    <div className="rounded-md border border-orange-200/60 bg-orange-50/30 p-3 dark:border-orange-900/50 dark:bg-orange-950/10">
      <div className="flex flex-wrap items-center gap-2">
        <IconTool className="h-4 w-4 shrink-0 text-orange-500" />
        <Badge variant="outline">{call.name || 'unknown'}</Badge>
        {call.status && (
          <Badge
            variant={call.status === 'success' ? 'secondary' : 'outline'}
            className="text-xs"
          >
            {call.status}
          </Badge>
        )}
        {typeof call.duration_ms === 'number' && (
          <span className="text-xs text-muted-foreground">
            {call.duration_ms}ms
          </span>
        )}
        {call.timestamp && (
          <span className="text-xs text-muted-foreground">
            {formatDateTime(call.timestamp)}
          </span>
        )}
        {call.call_id && (
          <span className="font-mono text-xs text-muted-foreground">
            {truncate(call.call_id, 28)}
          </span>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          tool #{idx + 1}
        </span>
      </div>
      {hasDetail && (
        <details className="mt-2 pl-6 text-sm">
          <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
            Input / output
          </summary>
          <div className="mt-2 grid gap-2 md:grid-cols-2">
            {call.input && (
              <div>
                <div className="mb-1 text-xs font-medium">Input</div>
                <pre className="max-h-[260px] overflow-auto rounded bg-muted/30 p-2 text-xs whitespace-pre-wrap">
                  {formatToolCallField(call.input, 6000)}
                </pre>
              </div>
            )}
            {call.output && (
              <div>
                <div className="mb-1 text-xs font-medium">Output</div>
                <pre className="max-h-[260px] overflow-auto rounded bg-muted/30 p-2 text-xs whitespace-pre-wrap">
                  {formatToolCallField(call.output, 6000)}
                </pre>
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  );
}

function TraceViewer({
  view,
  toolCalls,
  loading,
  error,
  requested,
  onLoad,
}: {
  view: EvalFileViewResponse | null;
  toolCalls: EvalTraceToolCall[];
  loading: boolean;
  error: string | null;
  requested: boolean;
  onLoad: () => void;
}) {
  const [verbose, setVerbose] = useState(false);
  const messages = useMemo(
    () => (view ? parseTranscriptMessages(view.content) : []),
    [view]
  );
  const entries = useMemo(
    () => buildTraceEntries(messages, toolCalls, verbose),
    [messages, toolCalls, verbose]
  );

  const handleDownload = useCallback(() => {
    if (!view) return;
    const blob = new Blob([view.content], { type: 'application/x-ndjson' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = view.path.split('/').pop() || 'transcript.jsonl';
    a.click();
    URL.revokeObjectURL(url);
  }, [view]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle>Trace</CardTitle>
          <p className="text-sm text-muted-foreground">
            Session messages with optional tool call timeline.
          </p>
          {view && (
            <p className="text-xs text-muted-foreground">
              {messages.length} messages · {toolCalls.length} tool calls ·{' '}
              {view.size_bytes} bytes
            </p>
          )}
        </div>
        {view && requested && (
          <div className="flex items-center gap-2">
            <Button
              variant={verbose ? 'default' : 'outline'}
              size="sm"
              onClick={() => setVerbose(value => !value)}
            >
              <IconTool className="mr-2 h-4 w-4" />
              Verbose
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownload}>
              <IconDownload className="mr-2 h-4 w-4" />
              Download JSONL
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-[240px] w-full" />
        ) : error ? (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        ) : !requested ? (
          <div className="rounded-md border border-dashed p-4 text-sm">
            <p className="text-muted-foreground">
              Trace messages are loaded on demand to keep the page responsive.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={onLoad}
            >
              Load trace
            </Button>
          </div>
        ) : !view || entries.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No trace available.
          </div>
        ) : (
          <div className="max-h-[600px] overflow-auto rounded-md">
            <div className="space-y-3">
              {entries.map((entry, idx) =>
                entry.kind === 'message' ? (
                  <TranscriptMessageRow
                    key={`message:${transcriptMessageKey(entry.message)}:${idx}`}
                    msg={entry.message}
                    idx={idx}
                  />
                ) : (
                  <TraceToolCallRow
                    key={`tool:${entry.call.call_id || entry.call.name}:${entry.call.timestamp || idx}`}
                    call={entry.call}
                    idx={idx}
                  />
                )
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function artifactIcon(artifact: EvalArtifactRef) {
  const previewMode = previewModeForArtifact(artifact);
  if (previewMode === 'image')
    return <IconPhoto className="h-4 w-4 shrink-0 text-blue-500" />;
  if (previewMode === 'video')
    return <IconVideo className="h-4 w-4 shrink-0 text-purple-500" />;
  if (previewMode === 'text')
    return <IconFileText className="h-4 w-4 shrink-0 text-green-500" />;
  return <IconFile className="h-4 w-4 shrink-0 text-muted-foreground" />;
}

function normalizeArtifactKind(kind?: string) {
  return kind?.trim().toLowerCase() || '';
}

function isTextArtifactKind(kind?: string) {
  return ['text', 'json', 'yaml', 'yml', 'xml', 'csv', 'markdown'].includes(
    normalizeArtifactKind(kind)
  );
}

function isImageArtifactKind(kind?: string) {
  return ['image'].includes(normalizeArtifactKind(kind));
}

function isVideoArtifactKind(kind?: string) {
  return ['video'].includes(normalizeArtifactKind(kind));
}

function previewModeForArtifact(
  artifact: EvalArtifactRef
): 'text' | 'image' | 'video' | 'none' {
  if (
    isImageArtifactKind(artifact.kind) ||
    isImageArtifact(artifact.name) ||
    isImageArtifact(artifact.uri)
  ) {
    return 'image';
  }
  if (
    isVideoArtifactKind(artifact.kind) ||
    isVideoArtifact(artifact.name) ||
    isVideoArtifact(artifact.uri)
  ) {
    return 'video';
  }
  if (
    isTextArtifactKind(artifact.kind) ||
    isTextArtifact(artifact.name) ||
    isTextArtifact(artifact.uri)
  ) {
    return 'text';
  }
  return 'none';
}

function isPreviewableArtifact(artifact: EvalArtifactRef) {
  return previewModeForArtifact(artifact) !== 'none';
}

function ArtifactPreview({
  experimentId,
  trialId,
  artifact,
  textView,
  textLoading,
  textError,
  requested,
  onLoad,
}: {
  experimentId: string;
  trialId: string;
  artifact: EvalArtifactRef;
  textView: EvalFileViewResponse | null;
  textLoading: boolean;
  textError: string | null;
  requested: boolean;
  onLoad: () => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [blobLoading, setBlobLoading] = useState(false);
  const [blobError, setBlobError] = useState<string | null>(null);

  const previewMode = previewModeForArtifact(artifact);
  const isImage = previewMode === 'image';
  const isVideo = previewMode === 'video';

  useEffect(() => {
    if ((!isImage && !isVideo) || !requested) return;

    let cancelled = false;
    setBlobLoading(true);
    setBlobError(null);
    fetchEvalTrialArtifactRaw(experimentId, trialId, artifact.name)
      .then(blob => {
        if (cancelled) return;
        setBlobUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      })
      .catch(err => {
        if (cancelled) return;
        setBlobError(
          err instanceof Error ? err.message : 'Failed to load artifact'
        );
      })
      .finally(() => {
        if (!cancelled) setBlobLoading(false);
      });

    return () => {
      cancelled = true;
      setBlobUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [experimentId, trialId, artifact.name, isImage, isVideo, requested]);

  if (!requested) {
    return (
      <div className="rounded-md border border-dashed p-4 text-sm">
        <p className="text-muted-foreground">
          Preview is loaded on demand for this artifact.
        </p>
        <Button variant="outline" size="sm" className="mt-3" onClick={onLoad}>
          Load preview
        </Button>
      </div>
    );
  }

  if (isImage || isVideo) {
    if (blobLoading) return <Skeleton className="h-[300px] w-full" />;
    if (blobError)
      return (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {blobError}
        </div>
      );
    if (!blobUrl) return null;
    if (isImage)
      return (
        <div className="flex items-center justify-center rounded-md border bg-muted/10 p-4">
          <img
            src={blobUrl}
            alt={artifact.name}
            className="max-h-[400px] max-w-full rounded object-contain"
          />
        </div>
      );
    return (
      <div className="flex items-center justify-center rounded-md border bg-muted/10 p-4">
        <video
          src={blobUrl}
          controls
          className="max-h-[400px] max-w-full rounded"
        />
      </div>
    );
  }

  const mode = inferTextMode(textView);
  if (textLoading) return <Skeleton className="h-[240px] w-full" />;
  if (textError)
    return (
      <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
        {textError}
      </div>
    );
  if (!textView) return null;

  if (mode === 'markdown') {
    return (
      <div className="max-h-[360px] overflow-auto rounded-md border bg-muted/10 p-4">
        <div className="prose prose-sm max-w-none dark:prose-invert">
          <ReactMarkdown>{textView.content}</ReactMarkdown>
        </div>
      </div>
    );
  }
  return (
    <div className="max-h-[360px] overflow-auto rounded-md border bg-muted/10">
      <pre className="whitespace-pre-wrap break-words p-4 text-xs leading-5">
        {mode === 'structured'
          ? prettyJson(textView.content)
          : textView.content}
      </pre>
    </div>
  );
}

export const EvalDetailPage: React.FC = () => {
  const { experimentId } = useParams<{ experimentId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [experiment, setExperiment] = useState<EvalExperimentEnvelope | null>(
    null
  );
  const [trials, setTrials] = useState<EvalTrialEnvelope[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [experimentArtifactName, setExperimentArtifactName] = useState('');
  const [experimentArtifactView, setExperimentArtifactView] =
    useState<EvalFileViewResponse | null>(null);
  const [experimentArtifactLoading, setExperimentArtifactLoading] =
    useState(false);
  const [experimentArtifactError, setExperimentArtifactError] = useState<
    string | null
  >(null);

  const [trialTranscriptView, setTrialTranscriptView] =
    useState<EvalFileViewResponse | null>(null);
  const [trialTranscriptLoading, setTrialTranscriptLoading] = useState(false);
  const [trialTranscriptError, setTrialTranscriptError] = useState<
    string | null
  >(null);

  const [trialArtifactName, setTrialArtifactName] = useState('');
  const [trialArtifactView, setTrialArtifactView] =
    useState<EvalFileViewResponse | null>(null);
  const [trialArtifactLoading, setTrialArtifactLoading] = useState(false);
  const [trialArtifactError, setTrialArtifactError] = useState<string | null>(
    null
  );
  const [experimentArtifactRequested, setExperimentArtifactRequested] =
    useState(false);
  const [trialTranscriptRequested, setTrialTranscriptRequested] =
    useState(false);
  const [trialArtifactRequested, setTrialArtifactRequested] = useState(false);

  const load = useCallback(
    async (background = false) => {
      if (!experimentId) {
        setError('Missing experiment id');
        setLoading(false);
        return;
      }

      if (background) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const [experimentData, trialData] = await Promise.all([
          fetchEvalExperiment(experimentId),
          fetchEvalTrialEnvelopes(experimentId),
        ]);
        trialData.sort((left, right) => {
          const leftAttempt = left.record.attempt_index || 0;
          const rightAttempt = right.record.attempt_index || 0;
          if (leftAttempt !== rightAttempt) {
            return leftAttempt - rightAttempt;
          }
          return (
            new Date(left.record.started_at || 0).getTime() -
            new Date(right.record.started_at || 0).getTime()
          );
        });

        setExperiment(experimentData);
        setTrials(trialData);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load eval detail'
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [experimentId]
  );

  useEffect(() => {
    load();
  }, [load]);

  const selectedTrialId = searchParams.get('trial') || '';
  const selectedTrial = useMemo(() => {
    if (selectedTrialId) {
      return trials.find(trial => trial.record.id === selectedTrialId) || null;
    }
    return trials[0] || null;
  }, [selectedTrialId, trials]);
  const selectedTrialArtifactName = useMemo(() => {
    const artifactRefs = selectedTrial?.record.artifact_refs || [];
    if (artifactRefs.length === 0) {
      return '';
    }

    return artifactRefs.some(artifact => artifact.name === trialArtifactName)
      ? trialArtifactName
      : artifactRefs[0]?.name || '';
  }, [selectedTrial, trialArtifactName]);
  const selectedTrialArtifact = useMemo(() => {
    if (!selectedTrialArtifactName) {
      return null;
    }
    return (
      selectedTrial?.record.artifact_refs?.find(
        artifact => artifact.name === selectedTrialArtifactName
      ) || null
    );
  }, [selectedTrial, selectedTrialArtifactName]);
  const selectedTrialArtifactPreviewMode = useMemo(
    () =>
      selectedTrialArtifact
        ? previewModeForArtifact(selectedTrialArtifact)
        : 'none',
    [selectedTrialArtifact]
  );

  useEffect(() => {
    if (!selectedTrial && trials.length === 0) {
      return;
    }
    if (!selectedTrial && trials[0]) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('trial', trials[0].record.id);
      setSearchParams(nextParams, { replace: true });
      return;
    }
  }, [searchParams, selectedTrial, setSearchParams, trials]);

  useEffect(() => {
    const firstArtifact = experiment?.artifacts?.[0]?.name || '';
    setExperimentArtifactName(current =>
      current &&
      experiment?.artifacts?.some(artifact => artifact.name === current)
        ? current
        : firstArtifact
    );
  }, [experiment]);

  useEffect(() => {
    setExperimentArtifactRequested(false);
    setExperimentArtifactView(null);
    setExperimentArtifactError(null);
    setExperimentArtifactLoading(false);
  }, [experimentArtifactName, experimentId]);

  useEffect(() => {
    if (
      !experimentId ||
      !experimentArtifactName ||
      !experimentArtifactRequested
    ) {
      setExperimentArtifactView(null);
      return;
    }

    let cancelled = false;
    setExperimentArtifactLoading(true);
    setExperimentArtifactError(null);
    fetchEvalExperimentArtifactView(experimentId, experimentArtifactName)
      .then(view => {
        if (cancelled) {
          return;
        }
        setExperimentArtifactView(view);
      })
      .catch(err => {
        if (cancelled) {
          return;
        }
        setExperimentArtifactError(
          err instanceof Error
            ? err.message
            : 'Failed to load experiment artifact'
        );
      })
      .finally(() => {
        if (!cancelled) {
          setExperimentArtifactLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [experimentArtifactName, experimentArtifactRequested, experimentId]);

  useEffect(() => {
    setTrialTranscriptRequested(false);
    setTrialTranscriptView(null);
    setTrialTranscriptError(null);
    setTrialTranscriptLoading(false);
  }, [experimentId, selectedTrial?.record.id]);

  useEffect(() => {
    if (
      !experimentId ||
      !selectedTrial?.record.id ||
      !trialTranscriptRequested
    ) {
      setTrialTranscriptView(null);
      return;
    }

    let cancelled = false;
    setTrialTranscriptLoading(true);
    setTrialTranscriptError(null);
    fetchEvalTrialTranscriptView(experimentId, selectedTrial.record.id)
      .then(view => {
        if (cancelled) {
          return;
        }
        setTrialTranscriptView(view);
      })
      .catch(err => {
        if (cancelled) {
          return;
        }
        setTrialTranscriptError(
          err instanceof Error ? err.message : 'Failed to load trace'
        );
      })
      .finally(() => {
        if (!cancelled) {
          setTrialTranscriptLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [experimentId, selectedTrial?.record.id, trialTranscriptRequested]);

  useEffect(() => {
    setTrialArtifactRequested(false);
    setTrialArtifactView(null);
    setTrialArtifactError(null);
    setTrialArtifactLoading(false);
  }, [experimentId, selectedTrial?.record.id, selectedTrialArtifactName]);

  useEffect(() => {
    if (
      !experimentId ||
      !selectedTrial?.record.id ||
      !selectedTrialArtifactName ||
      !trialArtifactRequested ||
      selectedTrialArtifactPreviewMode !== 'text'
    ) {
      setTrialArtifactView(null);
      return;
    }

    let cancelled = false;
    setTrialArtifactLoading(true);
    setTrialArtifactError(null);
    fetchEvalTrialArtifactView(
      experimentId,
      selectedTrial.record.id,
      selectedTrialArtifactName
    )
      .then(view => {
        if (cancelled) {
          return;
        }
        setTrialArtifactView(view);
      })
      .catch(err => {
        if (cancelled) {
          return;
        }
        setTrialArtifactError(
          err instanceof Error ? err.message : 'Failed to load trial artifact'
        );
      })
      .finally(() => {
        if (!cancelled) {
          setTrialArtifactLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    experimentId,
    selectedTrial?.record.id,
    selectedTrialArtifactName,
    selectedTrialArtifactPreviewMode,
    trialArtifactRequested,
  ]);

  const continuousLearningTrends = useMemo(
    () => buildContinuousLearningTrends(trials),
    [trials]
  );

  if (loading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-[520px] w-full" />
      </div>
    );
  }

  if (error || !experiment) {
    return (
      <div className="space-y-4 p-6">
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error || 'Experiment not found'}
        </div>
        <Button variant="outline" onClick={() => navigate('/evals')}>
          <IconArrowLeft className="mr-2 h-4 w-4" />
          Back to evals
        </Button>
      </div>
    );
  }

  const summary = experiment.summary;
  const revisionSet = experiment.record.revision_set || {};
  const selectedTrialArtifacts = selectedTrial?.record.artifact_refs || [];
  const selectedTrialGraders = selectedTrial?.record.grader_results || [];
  const selectedDeliveredFiles = parseDeliveredFilePaths(selectedTrial?.record);
  const gradingSummary = summarizeExperimentGrading(trials);
  const experimentCurveMetrics = extractMetricPairs(
    experiment.record.aggregate_metrics
  );
  const taskAggregates = experiment.record.task_aggregates || [];
  const taskCurveKeys = collectCurveKeys(taskAggregates);

  const breadcrumbs = [
    { label: 'Evals', to: '/evals' },
    { label: experiment.record.id },
  ];

  const suiteRefs =
    experiment?.record.suite_refs && experiment.record.suite_refs.length > 0
      ? experiment.record.suite_refs
      : experiment?.record.suite_ref
        ? [experiment.record.suite_ref]
        : [];

  return (
    <div className="space-y-6 p-6 overflow-y-auto h-full">
      <Breadcrumbs items={breadcrumbs} />

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Experiment Detail</h1>
          <p className="text-sm text-muted-foreground">
            {experiment.record.id}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/evals')}>
            <IconArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button
            variant="outline"
            onClick={() => load(true)}
            disabled={refreshing}
          >
            <IconRefresh className="mr-2 h-4 w-4" />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">
              Suite
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-medium">
              {suiteRefs.length > 0 ? suiteRefs.join(', ') : '—'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">
              Adapter
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <Badge variant="outline">{summary?.adapter || 'unknown'}</Badge>
            <div className="text-sm text-muted-foreground">
              {summary?.environment_mode || '—'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">
              Trials
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {summary?.trial_count ?? trials.length}
            </div>
            <div className="text-sm text-muted-foreground">
              {summary?.task_count ?? '—'} tasks · max parallelism{' '}
              {summary?.max_parallelism ?? '—'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">
              Grading
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {gradingSummary.gradedTrialCount > 0
                ? `${gradingSummary.passingTrialCount}/${gradingSummary.gradedTrialCount}`
                : '—'}
            </div>
            <div className="text-sm text-muted-foreground">
              passed trials · avg score{' '}
              {formatScore(gradingSummary.averageScore)}
            </div>
            {experimentCurveMetrics.length > 0 && (
              <div className="text-sm text-muted-foreground">
                {experimentCurveMetrics.map(([key, value], idx) => (
                  <span key={key}>
                    {idx > 0 && ' · '}
                    {formatCurveLabel(key)} {formatScore(value)}
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">
            Received
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="font-medium">
            {formatDateTime(experiment.received_at)}
          </div>
          <div className="text-sm text-muted-foreground">
            idempotency {experiment.idempotency_key}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <Card>
          <CardHeader>
            <CardTitle>Revision Set</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            {(
              [
                ['evals_sha', revisionSet.evals_sha],
                ['willow_sha', revisionSet.willow_sha],
                ['cueboard_sha', revisionSet.cueboard_sha],
                ['runner_sha', revisionSet.runner_sha],
              ] as const
            ).map(([label, sha]) => (
              <div key={label} className="min-w-0">
                <Label className="text-muted-foreground">{label}</Label>
                <p
                  className="truncate font-mono text-sm"
                  title={sha || undefined}
                >
                  {sha || '—'}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Experiment Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">eval mode:</span>{' '}
              <span className="font-mono">
                {summary?.eval_mode || 'standard'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">upload route:</span>{' '}
              <span className="font-mono">{summary?.upload_route || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">trial route:</span>{' '}
              <span className="font-mono">
                {summary?.trial_upload_route || '—'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">
                transcript strategy:
              </span>{' '}
              <span>{summary?.transcript_strategy || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">supports run:</span>{' '}
              {summary?.supports_run ? 'yes' : 'no'}
            </div>
            {experimentCurveMetrics.length > 0 && (
              <div className="space-y-1 pt-2">
                <span className="text-muted-foreground">pass curves:</span>
                {experimentCurveMetrics.map(([key, value]) => (
                  <div key={key} className="font-mono text-xs">
                    {formatCurveLabel(key)}: {formatScore(value)}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Task Aggregates</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Task</TableHead>
                <TableHead>Trials</TableHead>
                <TableHead>Pass Rate</TableHead>
                <TableHead>Avg Score</TableHead>
                <TableHead>Avg Tokens</TableHead>
                {taskCurveKeys.map(key => (
                  <TableHead key={key}>{formatCurveLabel(key)}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {taskAggregates.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5 + taskCurveKeys.length}
                    className="py-8 text-center"
                  >
                    <p className="text-muted-foreground">
                      No task aggregates recorded
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                taskAggregates.map((task: EvalTaskAggregate) => (
                  <TableRow key={task.task_ref}>
                    <TableCell>
                      <div className="font-medium">
                        {formatTaskLabel(task.task_ref)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {task.task_ref}
                      </div>
                    </TableCell>
                    <TableCell>
                      {task.passing_trial_count}/{task.trial_count}
                    </TableCell>
                    <TableCell>
                      {formatScore(task.aggregate_metrics?.pass_rate)}
                    </TableCell>
                    <TableCell>
                      {formatScore(task.average_trial_score)}
                    </TableCell>
                    <TableCell>
                      {formatAverageTokens(
                        task.aggregate_metrics?.average_total_tokens
                      )}
                    </TableCell>
                    {taskCurveKeys.map(key => (
                      <TableCell key={key}>
                        {formatScore(task.aggregate_metrics?.[key])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {continuousLearningTrends.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Continuous Learning Trend</CardTitle>
            <p className="text-sm text-muted-foreground">
              Trial score sequence for tasks that reused one Willow agent across
              sessions.
            </p>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Task</TableHead>
                  <TableHead>Sequence</TableHead>
                  <TableHead>Delta</TableHead>
                  <TableHead>Agent Group</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {continuousLearningTrends.map(trend => (
                  <TableRow key={trend.sequenceKey}>
                    <TableCell className="min-w-[260px]">
                      <div className="font-medium">{trend.taskLabel}</div>
                      <div className="break-all text-xs text-muted-foreground">
                        {trend.taskRef}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        {trend.points.map(point => (
                          <button
                            key={point.trialId}
                            type="button"
                            className="min-w-[92px] rounded-md border px-2 py-1 text-left hover:bg-muted/50"
                            onClick={() => {
                              const nextParams = new URLSearchParams(
                                searchParams
                              );
                              nextParams.set('trial', point.trialId);
                              setSearchParams(nextParams);
                            }}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs text-muted-foreground">
                                Trial {point.index}
                                {point.total || trend.total
                                  ? `/${point.total || trend.total}`
                                  : ''}
                              </span>
                              {point.pass !== null && (
                                <Badge
                                  variant={
                                    point.pass ? 'default' : 'destructive'
                                  }
                                  className="px-1 py-0 text-[10px]"
                                >
                                  {point.pass ? 'pass' : 'fail'}
                                </Badge>
                              )}
                            </div>
                            <div className="mt-1 font-mono text-sm">
                              {formatScore(point.score)}
                            </div>
                            <div className="text-[11px] text-muted-foreground">
                              {point.status || 'unknown'}
                            </div>
                          </button>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {trend.delta === null ? (
                        <span className="text-sm text-muted-foreground">—</span>
                      ) : (
                        <Badge
                          variant={trend.delta >= 0 ? 'default' : 'destructive'}
                        >
                          {trend.delta >= 0 ? '+' : ''}
                          {formatScore(trend.delta)}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-mono text-xs break-all">
                        {trend.agentGroupId || '—'}
                      </div>
                      <div className="font-mono text-xs text-muted-foreground break-all">
                        {trend.agentId || '—'}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle>Experiment Artifacts</CardTitle>
            <p className="text-sm text-muted-foreground">
              Dispatch manifest and prepared execution snapshots.
            </p>
          </div>
          <div className="w-full max-w-sm">
            <Select
              value={experimentArtifactName}
              onValueChange={setExperimentArtifactName}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select experiment artifact" />
              </SelectTrigger>
              <SelectContent>
                {(experiment.artifacts || []).map(artifact => (
                  <SelectItem key={artifact.name} value={artifact.name}>
                    {artifact.name} · {artifact.kind}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <FileViewer
            title={experimentArtifactName || 'Artifact'}
            view={experimentArtifactView}
            loading={experimentArtifactLoading}
            error={experimentArtifactError}
            emptyState={
              experimentArtifactName ? (
                <div className="rounded-md border border-dashed p-4 text-sm">
                  <p className="text-muted-foreground">
                    Preview is loaded on demand for experiment artifacts.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => setExperimentArtifactRequested(true)}
                  >
                    Load preview
                  </Button>
                </div>
              ) : undefined
            }
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Trials</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Attempt</TableHead>
                <TableHead>Task</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Grading</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Started</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trials.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center">
                    <p className="text-muted-foreground">No trials found</p>
                  </TableCell>
                </TableRow>
              ) : (
                trials.map(trial => (
                  <TableRow
                    key={trial.record.id}
                    className="cursor-pointer hover:bg-muted/40"
                    data-state={
                      selectedTrial?.record.id === trial.record.id
                        ? 'selected'
                        : undefined
                    }
                    onClick={() => {
                      const nextParams = new URLSearchParams(searchParams);
                      nextParams.set('trial', trial.record.id);
                      setSearchParams(nextParams);
                    }}
                  >
                    <TableCell>#{trial.record.attempt_index ?? 1}</TableCell>
                    <TableCell>
                      <div className="font-medium">
                        {trial.record.task_ref || '—'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {trial.record.environment_id || '—'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          trial.record.status === 'completed'
                            ? 'default'
                            : trial.record.status === 'failed'
                              ? 'destructive'
                              : 'secondary'
                        }
                      >
                        {trial.record.status || 'unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {trial.record.grader_results?.length ? (
                        <div className="space-y-1">
                          <Badge
                            variant={
                              isTrialPassing(trial.record)
                                ? 'default'
                                : 'destructive'
                            }
                          >
                            {isTrialPassing(trial.record) ? 'pass' : 'fail'}
                          </Badge>
                          <div className="text-xs text-muted-foreground">
                            {formatScore(averageTrialScore(trial.record))} ·{' '}
                            {trial.record.grader_results.length} grader
                            {trial.record.grader_results.length > 1 ? 's' : ''}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-mono text-xs">
                        {trial.record.agent_id || '—'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {trial.record.session_id || '—'}
                      </div>
                    </TableCell>
                    <TableCell>
                      {formatDuration(
                        trial.record.started_at,
                        trial.record.completed_at
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDateTime(trial.record.started_at)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {selectedTrial && (
        <>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">
                  Trial Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div>
                  <Label className="text-muted-foreground">Task</Label>
                  <p className="text-sm break-all">
                    {selectedTrial.record.task_ref || '—'}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Environment</Label>
                  <p className="text-sm break-all">
                    {selectedTrial.record.environment_id || '—'}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">
                  Agent / Session
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div>
                  <Label className="text-muted-foreground">Agent</Label>
                  <p className="font-mono text-xs break-all">
                    {selectedTrial.record.agent_id || '—'}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Session</Label>
                  <p className="font-mono text-xs break-all">
                    {selectedTrial.record.session_id || '—'}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">
                  Token Usage
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Prompt</span>
                  <span>
                    {formatCount(selectedTrial.record.usage?.prompt_tokens)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Completion</span>
                  <span>
                    {formatCount(selectedTrial.record.usage?.completion_tokens)}
                  </span>
                </div>
                <div className="flex justify-between font-medium">
                  <span className="text-muted-foreground">Total</span>
                  <span>
                    {formatCount(selectedTrial.record.usage?.total_tokens)}
                  </span>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">
                  Timing
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Duration</span>
                  <span>
                    {formatDuration(
                      selectedTrial.record.started_at,
                      selectedTrial.record.completed_at
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Started</span>
                  <span className="text-xs">
                    {formatDateTime(selectedTrial.record.started_at)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Outcome Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm whitespace-pre-wrap">
                {extractOutcomeText(selectedTrial.record.summary)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Grader Results</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedTrialGraders.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  No grader results recorded for this trial.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Grader</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Pass</TableHead>
                      <TableHead>Reasoning</TableHead>
                      <TableHead className="w-[88px]">Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedTrialGraders.map((grader: EvalGraderResult) => (
                      <TableRow key={grader.grader_id}>
                        <TableCell className="font-medium">
                          {formatGraderLabel(grader.grader_id)}
                        </TableCell>
                        <TableCell>{grader.type || '—'}</TableCell>
                        <TableCell>{grader.status || '—'}</TableCell>
                        <TableCell>{formatScore(grader.score)}</TableCell>
                        <TableCell>
                          <Badge
                            variant={grader.pass ? 'default' : 'destructive'}
                          >
                            {grader.pass ? 'pass' : 'fail'}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[300px] truncate text-xs text-muted-foreground">
                          {grader.reasoning || '—'}
                        </TableCell>
                        <TableCell>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                aria-label="View score details"
                                className="text-muted-foreground hover:text-foreground"
                              >
                                <IconInfoCircle className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent
                              side="left"
                              className="max-h-[400px] max-w-[480px] overflow-auto bg-popover text-popover-foreground border shadow-lg"
                            >
                              <div className="space-y-2 p-1 text-xs">
                                <div className="font-medium">Score details</div>
                                <div className="grid gap-2 sm:grid-cols-2">
                                  <div>
                                    <div className="mb-1 font-medium">
                                      Score
                                    </div>
                                    <div className="rounded bg-muted/20 p-2">
                                      {formatScore(grader.score)}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="mb-1 font-medium">Pass</div>
                                    <div className="rounded bg-muted/20 p-2">
                                      {grader.pass ? 'pass' : 'fail'}
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <div className="mb-1 font-medium">
                                    Grader ID
                                  </div>
                                  <pre className="rounded bg-muted/20 p-2 text-xs">
                                    {grader.grader_id}
                                  </pre>
                                </div>
                                <div>
                                  <div className="mb-1 font-medium">
                                    Reasoning
                                  </div>
                                  <pre className="max-h-[240px] overflow-auto rounded bg-muted/20 p-2 text-xs whitespace-pre-wrap">
                                    {grader.reasoning || '—'}
                                  </pre>
                                </div>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <TraceViewer
            view={trialTranscriptView}
            toolCalls={selectedTrial.record.tool_calls || []}
            loading={trialTranscriptLoading}
            error={trialTranscriptError}
            requested={trialTranscriptRequested}
            onLoad={() => setTrialTranscriptRequested(true)}
          />

          {(selectedTrial.record.skill_reads || []).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Skill Reads</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {(selectedTrial.record.skill_reads || []).map(read => (
                    <div key={`${read.path}-${read.timestamp || ''}`}>
                      <div className="font-mono text-xs break-all">
                        {read.path}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatDateTime(read.timestamp)}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Delivered Files</CardTitle>
              <p className="text-sm text-muted-foreground">
                File refs sent to the user through Willow delivery messages.
              </p>
            </CardHeader>
            <CardContent>
              {selectedDeliveredFiles.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  No delivered files recorded for this trial.
                </div>
              ) : (
                <div className="max-h-[220px] overflow-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Path</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedDeliveredFiles.map(path => (
                        <TableRow key={path}>
                          <TableCell className="break-all font-mono text-xs">
                            {path}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Trial Artifacts</CardTitle>
              <p className="text-sm text-muted-foreground">
                Captured files returned by the trial.
              </p>
            </CardHeader>
            <CardContent>
              {selectedTrialArtifacts.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  No artifacts captured for this trial.
                </div>
              ) : (
                <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
                  <div className="max-h-[420px] overflow-auto rounded-md border">
                    <div className="space-y-0.5 p-2">
                      {selectedTrialArtifacts.map(artifact => (
                        <button
                          key={artifact.name}
                          type="button"
                          className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-muted/60 ${
                            selectedTrialArtifactName === artifact.name
                              ? 'bg-muted font-medium'
                              : ''
                          }`}
                          onClick={() => setTrialArtifactName(artifact.name)}
                        >
                          {artifactIcon(artifact)}
                          <div className="min-w-0 flex-1">
                            <div className="truncate">{artifact.name}</div>
                            <div className="truncate text-xs text-muted-foreground">
                              {artifact.kind}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    {selectedTrialArtifactName ? (
                      (() => {
                        const activeArtifact = selectedTrialArtifacts.find(
                          a => a.name === selectedTrialArtifactName
                        );
                        if (!activeArtifact) return null;
                        const handleDownloadArtifact = () => {
                          fetchEvalTrialArtifactRaw(
                            experimentId!,
                            selectedTrial.record.id,
                            activeArtifact.name
                          ).then(blob => {
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download =
                              activeArtifact.uri.split('/').pop() ||
                              activeArtifact.name;
                            a.click();
                            URL.revokeObjectURL(url);
                          });
                        };
                        if (!isPreviewableArtifact(activeArtifact)) {
                          return (
                            <div className="rounded-md border bg-muted/10 p-4 text-sm text-muted-foreground">
                              <p className="font-medium text-foreground">
                                {activeArtifact.name}
                              </p>
                              <p>Kind: {activeArtifact.kind}</p>
                              <p className="break-all text-xs">
                                {activeArtifact.uri}
                              </p>
                              <Button
                                variant="outline"
                                size="sm"
                                className="mt-3"
                                onClick={handleDownloadArtifact}
                              >
                                <IconDownload className="mr-2 h-4 w-4" />
                                Download
                              </Button>
                            </div>
                          );
                        }
                        return (
                          <div>
                            <ArtifactPreview
                              experimentId={experimentId!}
                              trialId={selectedTrial.record.id}
                              artifact={activeArtifact}
                              textView={trialArtifactView}
                              textLoading={trialArtifactLoading}
                              textError={trialArtifactError}
                              requested={trialArtifactRequested}
                              onLoad={() => setTrialArtifactRequested(true)}
                            />
                            <div className="mt-2 flex justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleDownloadArtifact}
                              >
                                <IconDownload className="mr-2 h-4 w-4" />
                                Download
                              </Button>
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                        Select an artifact to preview.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Environment Metadata</CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(selectedTrial.record.environment_metadata || {})
                .length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  No environment metadata
                </div>
              ) : (
                <div className="max-h-[300px] overflow-auto">
                  <div className="space-y-2">
                    {Object.entries(
                      selectedTrial.record.environment_metadata || {}
                    ).map(([key, value]) => (
                      <div
                        key={key}
                        className="grid grid-cols-[160px_1fr] gap-3 text-xs"
                      >
                        <span
                          className="truncate font-mono text-muted-foreground"
                          title={key}
                        >
                          {key}
                        </span>
                        <span className="break-all">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
